import { z } from "zod";
import { competenciaSchema } from "./competencia";
import type { TipoFinanceiro } from "./lancamento";
import type { LinhaDoDre } from "./linha-do-dre";
import {
  ID_DA_PROVISAO,
  ID_DO_AJUSTE_DO_TRIMESTRE,
  ID_DO_IMPOSTO_ESTIMADO,
  ID_DO_IR_ESTIMADO,
  ID_DO_MATERIAL_CONSUMIDO,
  ID_DO_RETRABALHO,
  saidaSoDeCaixa,
  type ConfigDoDre,
  type Dre,
} from "./dre";

// ─── Do resultado ao caixa ───────────────────────────────────────────────

/** Um movimento de caixa do mês (pago ou recebido nele), com a categoria. */
export type MovimentoDeCaixa = { tipo: TipoFinanceiro; origem: string; valorCents: number; categoria: { nome: string; linhaDoDre: LinhaDoDre | null } };

export type PonteDoCaixa = {
  resultadoCents: number;
  /** Entrou no resultado sem sair dinheiro: depreciação, provisão, estimativas, material consumido. */
  semDinheiroCents: number;
  /** Recebido dos clientes no mês menos o faturado no mês. */
  recebidoMenosFaturadoCents: number;
  /** Despesas e receitas lançadas no mês menos as pagas no mês (contas a pagar, contas de outros meses). */
  contasCents: number;
  /** Pago no mês sem entrar no resultado: compras de material, 13º e férias provisionados, DARF. */
  soDeCaixaCents: number;
  /** Retirada, investimento, empréstimo. */
  foraDoResultadoCents: number;
  /** = recebido − pago no mês. */
  variacaoDoCaixaCents: number;
};

const NAO_E_DINHEIRO = new Set([ID_DO_MATERIAL_CONSUMIDO, ID_DO_RETRABALHO, ID_DO_IMPOSTO_ESTIMADO, ID_DA_PROVISAO, ID_DO_IR_ESTIMADO, ID_DO_AJUSTE_DO_TRIMESTRE]);
const somaDe = (cs: { id: string; valorCents: number }[]) => cs.filter((c) => NAO_E_DINHEIRO.has(c.id)).reduce((t, c) => t + c.valorCents, 0);

/**
 * Por que o caixa não mexeu o mesmo que o resultado. Cada movimento de caixa do mês cai numa linha
 * só, então a soma bate com o recebido − pago por construção.
 */
export function ponteDoCaixa(dre: Dre, caixa: MovimentoDeCaixa[], config: ConfigDoDre): PonteDoCaixa {
  const semDinheiroCents =
    somaDe(dre.custoDosServicos.categorias) + somaDe(dre.deducoes.categorias) + somaDe(dre.despesas.PESSOAL.categorias) + somaDe(dre.impostoSobreOLucro.categorias) + dre.depreciacao.valorCents;
  let recebido = 0;
  let resultadoPago = 0;
  let soDeCaixa = 0;
  let fora = 0;
  for (const m of caixa) {
    const sinal = m.tipo === "ENTRADA" ? 1 : -1;
    if (m.tipo === "ENTRADA" && m.origem === "RECEBIMENTO") recebido += m.valorCents;
    else if (m.categoria.linhaDoDre === "FORA_DO_RESULTADO") fora += sinal * m.valorCents;
    else if (saidaSoDeCaixa(m, config)) soDeCaixa += sinal * m.valorCents;
    else resultadoPago += sinal * m.valorCents;
  }
  // Saídas lançadas − entradas lançadas no resultado (sem o faturamento e sem o que não é dinheiro).
  const lancadoLiquido = dre.faturamentoCents - (dre.resultadoCents + semDinheiroCents);
  const contasCents = lancadoLiquido + resultadoPago;
  const ponte = {
    resultadoCents: dre.resultadoCents,
    semDinheiroCents,
    recebidoMenosFaturadoCents: recebido - dre.faturamentoCents,
    contasCents,
    soDeCaixaCents: soDeCaixa,
    foraDoResultadoCents: fora,
  };
  return { ...ponte, variacaoDoCaixaCents: Object.values(ponte).reduce((t, v) => t + v, 0) };
}

// ─── Rentabilidade por cliente ───────────────────────────────────────────

export type ReceitaDoCliente = { cliente: string; nome: string; receitaCents: number; materialCents: number };
export type RentabilidadeDoCliente = ReceitaDoCliente & {
  custoDiretoCents: number;
  deducaoCents: number;
  /** Margem de contribuição: receita − material − imposto sobre o faturamento. */
  margemCents: number;
  margemPct: number | null;
  /** O resto do resultado (pessoal, ocupação, outras receitas...) dividido pela participação na receita. */
  rateioCents: number;
  resultadoCents: number;
};

/**
 * Margem de contribuição por clínica e, com o rateio pela receita, o resultado de cada uma; os
 * resultados somam o resultado do DRE. Material pelas compras não tem cliente: entra rateado.
 */
export function rentabilidadePorCliente(clientes: ReceitaDoCliente[], dre: Dre, config: ConfigDoDre): RentabilidadeDoCliente[] {
  const total = clientes.reduce((t, c) => t + c.receitaCents, 0);
  const pelaFicha = !config.dreLigado || config.custoDoMaterial === "FICHA";
  const base = clientes.map((c) => {
    const parte = total > 0 ? c.receitaCents / total : 0;
    const custoDiretoCents = pelaFicha ? c.materialCents : Math.round(dre.custoDosServicos.valorCents * parte);
    // Desconto e imposto entram rateados pela receita (o desconto nem sempre tem cliente).
    const deducaoCents = Math.round((dre.deducoes.valorCents + dre.descontos.valorCents) * parte);
    return { ...c, parte, custoDiretoCents, deducaoCents, margemCents: c.receitaCents - custoDiretoCents - deducaoCents };
  });
  const resto = base.reduce((t, c) => t + c.margemCents, 0) - dre.resultadoCents;
  return base
    .map(({ parte, ...c }) => {
      const rateioCents = Math.round(resto * parte);
      return { ...c, margemPct: c.receitaCents > 0 ? c.margemCents / c.receitaCents : null, rateioCents, resultadoCents: c.margemCents - rateioCents };
    })
    .sort((a, b) => b.margemCents - a.margemCents);
}

// ─── Metas (orçado × realizado) ──────────────────────────────────────────

/** As linhas do DRE que aceitam meta; os subtotais saem delas. `entrada` = valor positivo. */
export const LINHAS_COM_META = [
  { chave: "faturamento", rotulo: "Faturamento da produção", entrada: true },
  { chave: "outrasReceitas", rotulo: "Outras receitas", entrada: true },
  { chave: "descontos", rotulo: "Descontos concedidos", entrada: false },
  { chave: "deducoes", rotulo: "Impostos sobre o faturamento", entrada: false },
  { chave: "custo", rotulo: "Custo dos serviços", entrada: false },
  { chave: "PESSOAL", rotulo: "Pessoal", entrada: false },
  { chave: "OCUPACAO", rotulo: "Ocupação e equipamentos", entrada: false },
  { chave: "ADMINISTRATIVA", rotulo: "Administrativas", entrada: false },
  { chave: "COMERCIAL", rotulo: "Comerciais e entrega", entrada: false },
  { chave: "depreciacao", rotulo: "Depreciação", entrada: false },
  { chave: "financeiro", rotulo: "Resultado financeiro (custo líquido)", entrada: false },
  { chave: "ir", rotulo: "IR e CSLL", entrada: false },
] as const;
export type ChaveDeMeta = (typeof LINHAS_COM_META)[number]["chave"];
const CHAVES_DE_META = LINHAS_COM_META.map((l) => l.chave) as [ChaveDeMeta, ...ChaveDeMeta[]];

/** Metas em valor positivo (o quanto se quer faturar ou gastar). Nulo apaga a meta da linha. */
export const metasSalvarSchema = z.object({
  competencias: z.array(competenciaSchema).min(1).max(12),
  // Parcial: só as linhas enviadas mudam.
  valores: z.partialRecord(z.enum(CHAVES_DE_META), z.number().int().min(0).max(1_000_000_000).nullable()),
});
export type MetasSalvarInput = z.infer<typeof metasSalvarSchema>;

/** Vários meses juntos (o modo Ano): soma cliente a cliente; a margem % é refeita sobre a soma. */
export function somarRentabilidades(meses: RentabilidadeDoCliente[][]): RentabilidadeDoCliente[] {
  const mapa = new Map<string, RentabilidadeDoCliente>();
  const campos = ["receitaCents", "materialCents", "custoDiretoCents", "deducaoCents", "margemCents", "rateioCents", "resultadoCents"] as const;
  for (const c of meses.flat()) {
    const atual = mapa.get(c.cliente);
    if (!atual) mapa.set(c.cliente, { ...c });
    else for (const k of campos) atual[k] += c[k];
  }
  return [...mapa.values()].map((c) => ({ ...c, margemPct: c.receitaCents > 0 ? c.margemCents / c.receitaCents : null })).sort((a, b) => b.margemCents - a.margemCents);
}

export function somarPontes(pontes: PonteDoCaixa[]): PonteDoCaixa {
  const soma = { resultadoCents: 0, semDinheiroCents: 0, recebidoMenosFaturadoCents: 0, contasCents: 0, soDeCaixaCents: 0, foraDoResultadoCents: 0, variacaoDoCaixaCents: 0 };
  for (const p of pontes) for (const k of Object.keys(soma) as (keyof PonteDoCaixa)[]) soma[k] += p[k];
  return soma;
}
